import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useRef
} from "react";
import { IconCategoryItem } from "./ChooseAppIconDialog";
import { FontIconName, TreeAccordion } from "client/Components";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { AppIconCategoryKind } from "client/Api/AppIcon/appIcon";

export type CategoryFilterHandle = {
	scrollCategoryIntoView: (id: AppIconCategoryKind) => void;
};

export type CategoryFilterProps = {
	categories: readonly IconCategoryItem[];
	selectedCategory: AppIconCategoryKind | null;
	onChange: (id: AppIconCategoryKind | null) => void;
};

export const CategoryFilter = forwardRef(
	(props: CategoryFilterProps, ref: Ref<CategoryFilterHandle>): ReactElement => {
		const {
			categories,
			selectedCategory,
			onChange
		} = props;

		const itemsRef: ReadonlyRefObject<Map<AppIconCategoryKind, HTMLElement>> = useRef<Map<AppIconCategoryKind, HTMLElement>>(new Map());

		const setItemRef = useCallback(
			(ref: HTMLElement | null, id: AppIconCategoryKind | null): void => {
				if (isNil(id)) {
					return;
				}

				if (!ref) {
					itemsRef.current
						.delete(id);
					return;
				}

				itemsRef.current
					.set(id, ref);
			},
			[]
		);

		const scrollCategoryIntoView = useCallback(
			(id: AppIconCategoryKind): void => {
				const targetItem: HTMLElement | nil = itemsRef.current
					.get(id);

				if (!targetItem) {
					console.error(`Category with id: ${id} doesn't have a ref`);
					return;
				}

				targetItem.scrollIntoView();
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): CategoryFilterHandle => ({
				scrollCategoryIntoView
			}),
			[scrollCategoryIntoView]
		);

		return (
			<TreeAccordion trackingId="icon-category-filter">
				{categories.map(
					x => (
						<TreeAccordion.Node key={getCategoryKey(x.id)}>
							<TreeAccordion.Item
								ref={ref => setItemRef(ref, x.id)}
								trackingId={generateTrackingId("item", `|${getCategoryKey(x.id)}`)}
								isSelected={x.id === selectedCategory}
								icon={isNil(x.id)
									? "window-restore"
									: iconCategoryToIconMap[x.id]
								}
								title={x.name}
								onClick={() => onChange(x.id)}

							>
								{x.name}
							</TreeAccordion.Item>
						</TreeAccordion.Node>
					)
				)}
			</TreeAccordion>
		);
	}
);
CategoryFilter.displayName = "CategoryFilter";

function getCategoryKey(
	id: AppIconCategoryKind | null
): string | number {
	return isNotNil(id)
	? id
	: "all";
}

const iconCategoryToIconMap: Record<AppIconCategoryKind, FontIconName> = {
	[AppIconCategoryKind.Adobe]: "adobe",
	[AppIconCategoryKind.Microsoft]: "windows8",
	[AppIconCategoryKind.Brands]: "copyright",
	[AppIconCategoryKind.Devices]: "mobile-notch",
	[AppIconCategoryKind.Finance]: "coins",
	[AppIconCategoryKind.Social]: "user-group",
	[AppIconCategoryKind.Healthcare]: "briefcase-medical",
	[AppIconCategoryKind.Education]: "graduation-cap",
	[AppIconCategoryKind.Transport]: "car-side",
	[AppIconCategoryKind.Google]: "google",
	[AppIconCategoryKind.Weather]: "brightness",
	[AppIconCategoryKind.BusinessApps]: "buildings",
	[AppIconCategoryKind.Documents]: "folder-open",
	[AppIconCategoryKind.CRM]: "address-card",
	[AppIconCategoryKind.Development]: "code",
	[AppIconCategoryKind.Browsers]: "window",
	[AppIconCategoryKind.Time]: "clock",
	[AppIconCategoryKind.Sounds]: "volume",
	[AppIconCategoryKind.Other]: "compass",
	[AppIconCategoryKind.Custom]: "wrench"
};